import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Plus,
  BookOpen,
  Database,
  TrendingUp,
  Filter,
  Clock,
  Shuffle,
  MoreVertical,
  Pencil,
  FolderInput,
  Trash2,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import WorkspacePill from '../../components/layout/WorkspacePill';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import NameModal from '../../components/ui/NameModal';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useAuth, usePermissions } from '../../contexts/AuthContext';
import { businessesApi, type BusinessDossier, type BusinessSource } from '../../services/businesses-api';
import { orgApi } from '../../services/org-api';
import DeletionImpactPreview, { type ImpactItem } from '../../components/settings/DeletionImpactPreview';
import type { OrgListItem, OrgSource } from '../../types';
import { AVAILABLE_INTEGRATIONS } from '../../config/integrations';

const BusinessesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useAuth();
  const { canManageOrganization } = usePermissions();
  const isAdmin = canManageOrganization();

  const { data: dossiers = [], isLoading } = useQuery({
    queryKey: ['businessesOverview'],
    queryFn: () => businessesApi.getOverview(),
  });

  // Management metadata (isDefault / canDelete / timezone / counts) is admin-only
  // and comes from the workspace-scoped org API. Members never fire this query and
  // see the read-only dossiers alone.
  const { data: orgs = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: orgApi.listOrganizations,
    enabled: isAdmin,
  });
  const orgById = useMemo(
    () => new Map<string, OrgListItem>(orgs.map((o) => [o.id, o])),
    [orgs],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [editOrg, setEditOrg] = useState<OrgListItem | null>(null);
  const [moveOrg, setMoveOrg] = useState<OrgListItem | null>(null);
  const [deleteOrg, setDeleteOrg] = useState<OrgListItem | null>(null);

  // Sources move between businesses and names/timezones change → refresh both the
  // dossier view and the mgmt list, plus auth (the switcher lists org names).
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['businessesOverview'] });
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
    refetchAuth();
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => orgApi.deleteOrganization(id),
    onSuccess: () => {
      toast.success('Business deleted');
      invalidate();
      setDeleteOrg(null);
    },
    // 400/409 (e.g. "still has data sources") surface via the axios interceptor;
    // keep the modal open.
  });

  const deleteImpact = useQuery({
    queryKey: ['orgDeletionImpact', deleteOrg?.id],
    queryFn: () => orgApi.getDeletionImpact(deleteOrg!.id),
    enabled: !!deleteOrg,
  });
  const deleteImpactItems: ImpactItem[] = deleteImpact.data
    ? [
        { label: 'Wiki pages removed', value: deleteImpact.data.wikiPages },
        { label: 'Semantic entries removed', value: deleteImpact.data.semanticEntries },
        { label: 'Pulse jobs removed', value: deleteImpact.data.pulseJobs },
      ]
    : [];
  const orgImpact = deleteImpact.data;
  const orgDeleteBlocked = !!orgImpact && !orgImpact.canDelete;

  return (
    <div className="max-w-6xl mx-auto px-6 pt-7 pb-24">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Businesses</h1>
        <div className="flex items-center gap-2.5">
          <WorkspacePill />
          {isAdmin && (
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-accent"
            >
              <Plus className="w-3.5 h-3.5" /> New business
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <LoadingSpinner />
        </div>
      ) : (
        dossiers.map((d) => (
          <Dossier
            key={d.id}
            dossier={d}
            mgmt={orgById.get(d.id)}
            onEdit={setEditOrg}
            onMove={setMoveOrg}
            onDelete={setDeleteOrg}
          />
        ))
      )}

      {createOpen && (
        <NameModal
          title="New business"
          submitText="Create"
          label="Business name"
          placeholder="e.g. Van Horne Restaurant"
          onClose={() => setCreateOpen(false)}
          onSubmit={async (name) => { await orgApi.createOrganization(name); toast.success('Business created'); invalidate(); }}
        />
      )}

      {editOrg && (
        <BusinessSettingsModal
          org={editOrg}
          onClose={() => setEditOrg(null)}
          onSaved={invalidate}
        />
      )}

      {moveOrg && (
        <MoveSourcesModal
          org={moveOrg}
          allOrgs={orgs}
          onClose={() => setMoveOrg(null)}
          onDone={invalidate}
        />
      )}

      {deleteOrg && (
        <Modal isOpen onClose={() => setDeleteOrg(null)} size="sm">
          <ModalHeader onClose={() => setDeleteOrg(null)}>Delete business</ModalHeader>
          <ModalBody>
            {deleteImpact.isLoading ? (
              <div className="py-6 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : orgDeleteBlocked ? (
              <div className="rounded-[10px] border border-warn/30 bg-warn/[0.06] p-3.5">
                <p className="text-[13.5px] font-semibold text-foreground">
                  “{deleteOrg.name}” can’t be deleted yet
                </p>
                <p className="text-[12.5px] text-muted-foreground mt-1">
                  Clear the following first, then delete it:
                </p>
                <ul className="mt-2.5 space-y-1.5 text-[12.5px] text-muted-foreground list-disc pl-4">
                  {orgImpact!.blockingSources.length > 0 && (
                    <li>
                      It still has {orgImpact!.blockingSources.length}{' '}
                      {orgImpact!.blockingSources.length === 1 ? 'data source' : 'data sources'} —
                      move {orgImpact!.blockingSources.length === 1 ? 'it' : 'them'} to another business or
                      delete {orgImpact!.blockingSources.length === 1 ? 'it' : 'them'} from Data sources:{' '}
                      <span className="text-foreground">
                        {orgImpact!.blockingSources.map((s) => s.name).join(', ')}
                      </span>
                      .
                    </li>
                  )}
                  {orgImpact!.homedUsers > 0 && (
                    <li>
                      {orgImpact!.homedUsers} {orgImpact!.homedUsers === 1 ? 'member is' : 'members are'} homed
                      in this business — reassign {orgImpact!.homedUsers === 1 ? 'them' : 'them'} to another one first.
                    </li>
                  )}
                  {orgImpact!.isLastOrg && (
                    <li>It’s your only business — a workspace must keep at least one.</li>
                  )}
                </ul>
              </div>
            ) : (
              <>
                <p className="text-muted-foreground">
                  Delete <strong className="text-foreground">{deleteOrg.name}</strong>? This can&apos;t be undone.
                </p>
                <DeletionImpactPreview
                  items={deleteImpactItems}
                  error={deleteImpact.isError}
                  className="mt-3"
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {orgDeleteBlocked ? (
              <>
                {orgImpact!.blockingSources.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => { setMoveOrg(deleteOrg); setDeleteOrg(null); }}
                  >
                    Move sources…
                  </Button>
                )}
                <Button variant="primary" onClick={() => setDeleteOrg(null)}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setDeleteOrg(null)} disabled={deleteMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  isLoading={deleteMutation.isPending}
                  disabled={deleteImpact.isLoading}
                  onClick={() => deleteMutation.mutate(deleteOrg.id)}
                >
                  Delete
                </Button>
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

const SEM_ROWS: Array<{ key: keyof BusinessDossier['semantics']['byType'] | 'glossary_maps'; label: string; icon: React.ComponentType<{ className?: string }>; tile: string }> = [
  { key: 'metric', label: 'Metrics', icon: TrendingUp, tile: 'bg-success/15 text-success' },
  { key: 'segment', label: 'Segments', icon: Filter, tile: 'bg-info/15 text-info' },
  { key: 'time_window', label: 'Time windows', icon: Clock, tile: 'bg-warn/15 text-warn' },
  { key: 'glossary', label: 'Glossary', icon: BookOpen, tile: 'bg-primary/15 text-primary' },
  { key: 'product_mapping', label: 'Mappings', icon: Shuffle, tile: 'bg-muted text-muted-foreground' },
];

const Dossier: React.FC<{
  dossier: BusinessDossier;
  mgmt?: OrgListItem;
  onEdit: (org: OrgListItem) => void;
  onMove: (org: OrgListItem) => void;
  onDelete: (org: OrgListItem) => void;
}> = ({ dossier: d, mgmt, onEdit, onMove, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const updatedBy =
    d.wiki.updatedByType === 'llm' ? 'Zamino AI' : d.wiki.updatedByType === 'human' || d.wiki.updatedByType === 'user' ? 'you' : 'Zamino';

  return (
    <section className="mt-6 rounded-[10px] border border-border bg-card shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3.5 px-5 py-4 border-b border-border">
        <div className="w-10 h-10 rounded-[10px] bg-primary/15 text-primary flex items-center justify-center text-[13px] font-bold flex-none">
          {monogram(d.name)}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[16px] font-semibold text-foreground truncate">{d.name}</div>
            {mgmt?.isDefault && (
              <span className="text-[10px] font-bold uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded flex-none">
                Default
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {d.sources.length} {d.sources.length === 1 ? 'source' : 'sources'} connected
          </div>
        </div>
        <div className="ml-auto flex items-center gap-5">
          <Stat v={String(d.sources.length)} k="Sources" />
          <Stat v={String(d.semantics.total)} k="Semantics" />
          {d.attention > 0 ? (
            <Stat v={String(d.attention)} k="Attention" warn />
          ) : (
            <Stat v={d.queries30d.toLocaleString()} k="Queries · 30d" />
          )}
        </div>

        {mgmt && (
          <div className="relative flex-none">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
              aria-label="Business actions"
            >
              <MoreVertical className="w-4 h-4 text-muted-foreground" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-52 bg-card rounded-lg shadow-lg border border-border z-20 py-1">
                  <MenuButton onClick={() => { onEdit(mgmt); setMenuOpen(false); }}>
                    <Pencil className="w-3.5 h-3.5 mr-2 inline" /> Business settings
                  </MenuButton>
                  <MenuButton onClick={() => { onMove(mgmt); setMenuOpen(false); }}>
                    <FolderInput className="w-3.5 h-3.5 mr-2 inline" /> Move sources…
                  </MenuButton>
                  <div className="border-t border-border my-1" />
                  <MenuButton
                    destructive
                    onClick={() => { onDelete(mgmt); setMenuOpen(false); }}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-2 inline" /> Delete
                  </MenuButton>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left: sources + semantics */}
        <div className="px-5 py-4 lg:border-r border-border">
          <SectionHead label="Data sources" to="/sources" linkText="Manage →" />
          {d.sources.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">No sources you have access to.</p>
          )}
          {d.sources.map((s) => (
            <SourceRowMini key={s.id} source={s} />
          ))}

          <div className="mt-4">
            <SectionHead label="Semantics" to={`/semantics`} linkText="View all →" />
            {SEM_ROWS.filter((r) => (d.semantics.byType as Record<string, number>)[r.key] > 0).map((r) => (
              <div key={r.key} className="flex items-center gap-2.5 py-1.5 text-[12.5px] text-foreground">
                <span className={clsx('w-[20px] h-[20px] rounded-md flex items-center justify-center flex-none', r.tile)}>
                  <r.icon className="w-2.5 h-2.5" />
                </span>
                {r.label}
                <span className="ml-auto font-semibold tabular-nums">
                  {(d.semantics.byType as Record<string, number>)[r.key]}
                </span>
              </div>
            ))}
            {d.semantics.total === 0 && (
              <p className="text-xs text-muted-foreground py-2">No definitions yet.</p>
            )}
            {d.semantics.proposed > 0 && (
              <Link to="/semantics" className="block text-[11.5px] font-semibold text-warn mt-1.5 hover:underline">
                ● {d.semantics.proposed} proposed await review
              </Link>
            )}
          </div>
        </div>

        {/* Right: wiki */}
        <div className="px-5 py-4 border-t lg:border-t-0 border-border">
          {d.wiki.exists && d.wiki.started ? (
            <>
              <SectionHead label={`Wiki — ${d.wiki.title ?? 'Business overview'}`} />
              <p className="text-[11.5px] text-muted-foreground mb-2.5">
                <b className="text-foreground">v{d.wiki.version}</b>
                {d.wiki.updatedAt && (
                  <>
                    {' '}· updated <b className="text-foreground">{new Date(d.wiki.updatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</b> by{' '}
                    <b className="text-foreground">{updatedBy}</b>
                  </>
                )}{' '}
                · read by the AI on every question about {d.name}
              </p>
              <div className="relative text-[13px] leading-relaxed text-foreground max-h-40 overflow-hidden">
                {d.wiki.excerpt}
                <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />
              </div>
            </>
          ) : (
            <>
              <SectionHead label="Wiki — Business overview" trailing={<span className="text-[12px] font-semibold text-warn">not started</span>} />
              <p className="rounded-lg border border-dashed border-border bg-muted/40 px-3.5 py-3 text-[12.5px] text-muted-foreground">
                Zamino hasn&apos;t written {d.name}&apos;s story yet — industry, products, customers, and
                seasonality are blank. Until then, the AI answers questions about {d.name} with less
                context.
              </p>
            </>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {d.wiki.exists && d.wiki.started ? (
              <>
                <WikiBtn primary icon={<BookOpen className="w-3.5 h-3.5" />} to={`/wiki/${d.wiki.pageId}`}>
                  Open wiki
                </WikiBtn>
                <WikiBtn to={`/wiki/${d.wiki.pageId}?view=edit`}>Edit</WikiBtn>
                <WikiBtn to={`/wiki/${d.wiki.pageId}?view=discuss`}>✦ Give feedback</WikiBtn>
              </>
            ) : (
              <>
                <WikiBtn
                  primary
                  to={d.wiki.pageId ? `/wiki/${d.wiki.pageId}?view=discuss&mode=interview` : undefined}
                >
                  ✦ Interview me and draft it
                </WikiBtn>
                <WikiBtn to={d.wiki.pageId ? `/wiki/${d.wiki.pageId}?view=edit` : undefined}>
                  Write manually
                </WikiBtn>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const SourceRowMini: React.FC<{ source: BusinessSource }> = ({ source: s }) => {
  const { formatRelativeTime } = useTimezone();
  const logo = AVAILABLE_INTEGRATIONS.find((i) => i.id === s.integrationId)?.logo;
  const problem = s.healthStatus !== 'healthy';
  const status = problem
    ? 'Reconnect'
    : s.mode !== 'ingestion'
      ? 'On demand'
      : s.lastSyncAt
        ? `Synced ${formatRelativeTime(s.lastSyncAt)}`
        : 'Not synced yet';
  return (
    <div className="flex items-center gap-2.5 py-1.5 text-[12.5px] text-foreground min-w-0">
      <span className="relative w-[22px] h-[22px] rounded-md overflow-hidden bg-muted flex items-center justify-center flex-none">
        {logo ? <img src={logo} alt="" className="w-[22px] h-[22px] object-contain" /> : <Database className="w-3 h-3 text-muted-foreground" />}
        <span
          className={clsx(
            'absolute -right-px -bottom-px w-2 h-2 rounded-sm border border-card',
            problem ? 'bg-warn' : 'bg-success',
          )}
        />
      </span>
      <span className="truncate">{s.name}</span>
      <span className={clsx('ml-auto text-[11px] flex-none', problem ? 'text-warn font-semibold' : 'text-muted-foreground')}>
        {status}
      </span>
    </div>
  );
};

const SectionHead: React.FC<{ label: string; to?: string; linkText?: string; trailing?: React.ReactNode }> = ({
  label,
  to,
  linkText,
  trailing,
}) => (
  <div className="flex items-center justify-between mb-1.5">
    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
    {to && linkText && (
      <Link to={to} className="text-[12px] font-semibold text-primary hover:underline">
        {linkText}
      </Link>
    )}
    {trailing}
  </div>
);

const Stat: React.FC<{ v: string; k: string; warn?: boolean }> = ({ v, k, warn }) => (
  <div className="text-right">
    <div className={clsx('text-[17px] font-semibold tabular-nums', warn ? 'text-warn' : 'text-foreground')}>{v}</div>
    <div className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">{k}</div>
  </div>
);

const WikiBtn: React.FC<{
  children: React.ReactNode;
  primary?: boolean;
  icon?: React.ReactNode;
  to?: string;
}> = ({ children, primary, icon, to }) => {
  const className = clsx(
    'inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-[12.5px] font-medium',
    primary ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-input bg-card text-foreground hover:bg-accent',
    !to && 'opacity-50 cursor-not-allowed',
  );
  if (!to) {
    return (
      <button disabled title="No wiki page yet for this business" className={className}>
        {icon}
        {children}
      </button>
    );
  }
  return (
    <Link to={to} className={className}>
      {icon}
      {children}
    </Link>
  );
};

// ─── Business settings modal (name + timezone) ───────────────────────────────

const BusinessSettingsModal: React.FC<{
  org: OrgListItem;
  onClose: () => void;
  onSaved: () => void;
}> = ({ org, onClose, onSaved }) => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [name, setName] = useState(org.name);
  const [timezone, setTimezone] = useState(org.defaultTimezone || browserTimezone);

  const timezones = useMemo(() => {
    const supported =
      typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['UTC'];
    const now = new Date();
    return supported
      .map((tz) => {
        let offset = '';
        try {
          const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'shortOffset',
          }).formatToParts(now);
          offset = parts.find((p) => p.type === 'timeZoneName')?.value || '';
        } catch {
          // ignore unknown zones
        }
        return { value: tz, label: offset ? `${tz} (${offset})` : tz };
      })
      .sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  const mutation = useMutation({
    mutationFn: () => {
      const updates: { name?: string; defaultTimezone?: string } = {};
      const cleanName = name.trim();
      if (cleanName !== org.name) updates.name = cleanName;
      if (timezone !== (org.defaultTimezone || browserTimezone)) updates.defaultTimezone = timezone;
      return orgApi.updateOrganization(org.id, updates);
    },
    onSuccess: () => {
      toast.success('Business updated');
      onSaved();
      onClose();
    },
  });

  const dirty = name.trim() !== org.name || timezone !== (org.defaultTimezone || browserTimezone);

  return (
    <Modal isOpen onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>Business settings</ModalHeader>
      <ModalBody className="space-y-4">
        <Input
          label="Business name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Inc."
          autoFocus
        />
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Default timezone</label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="block w-full rounded-lg text-sm bg-card border border-input text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/40 transition-colors"
          >
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1.5">
            Fallback timezone for users who haven&apos;t set one on their profile.
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!name.trim() || !dirty}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ─── Move sources modal ──────────────────────────────────────────────────────

const MoveSourcesModal: React.FC<{
  org: OrgListItem;
  allOrgs: OrgListItem[];
  onClose: () => void;
  onDone: () => void;
}> = ({ org, allOrgs, onClose, onDone }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetOrgId, setTargetOrgId] = useState('');

  const sourcesQuery = useQuery({
    queryKey: ['orgSources', org.id],
    queryFn: () => orgApi.listOrgSources(org.id),
  });
  const sources = sourcesQuery.data ?? [];
  const otherOrgs = allOrgs.filter((o) => o.id !== org.id);

  const move = useMutation({
    mutationFn: () => orgApi.moveSources(Array.from(selected), targetOrgId),
    onSuccess: (moved) => {
      toast.success(`Moved ${moved} ${moved === 1 ? 'source' : 'sources'}`);
      onDone();
      onClose();
    },
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>
        <div>
          <div>Move sources</div>
          <p className="text-sm font-normal text-muted-foreground mt-0.5">from {org.name}</p>
        </div>
      </ModalHeader>
      <ModalBody className="space-y-4">
        {sourcesQuery.isLoading ? (
          <div className="flex justify-center py-6"><LoadingSpinner /></div>
        ) : sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">This business has no data sources to move.</p>
        ) : (
          <>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Select sources ({selected.size})
              </p>
              <div className="rounded-lg border border-border divide-y divide-border max-h-64 overflow-y-auto">
                {sources.map((s: OrgSource) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggle(s.id)}
                      className="rounded border-input text-primary focus:ring-ring"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{s.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {s.sourceType.replace(/[-_]/g, ' ')}{!s.isActive && ' · inactive'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Move to</label>
              {otherOrgs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  There&apos;s no other business to move sources into. Create one first.
                </p>
              ) : (
                <select
                  value={targetOrgId}
                  onChange={(e) => setTargetOrgId(e.target.value)}
                  className="block w-full rounded-lg text-sm bg-card border border-input text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/40 transition-colors"
                >
                  <option value="">Select a business…</option>
                  {otherOrgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              )}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={move.isPending}>Cancel</Button>
        <Button
          onClick={() => move.mutate()}
          isLoading={move.isPending}
          disabled={selected.size === 0 || !targetOrgId}
          leftIcon={<FolderInput className="w-4 h-4" />}
        >
          Move {selected.size > 0 ? selected.size : ''}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const MenuButton: React.FC<{
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ onClick, destructive, disabled, title, children }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    title={title}
    className={clsx(
      'w-full text-left px-3 py-2 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
      destructive ? 'text-destructive hover:bg-destructive/15' : 'text-foreground hover:bg-accent',
      disabled && 'hover:bg-transparent',
    )}
  >
    {children}
  </button>
);

function monogram(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

export default BusinessesPage;
